import { EventType, Maybe } from '@varo/graphql-schema-types';
import useKardAttribution from 'features/rewards/hooks/useKardAttribution';
import { formatRewardsData } from 'features/rewards/RewardsHome/adapters/RewardsHomeFormatter';
import { useNearbySectionRenderer } from 'features/rewards/RewardsHome/hooks/useNearbySectionRenderer';
import useSelfFundedOffersV2 from 'features/rewards/RewardsHome/hooks/useSelfFundedOffersV2';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionedContentScreenModel } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';

import { useRewardsPagination } from './useRewardsPagination';
import { useRewardsQueries } from './useRewardsQueries';

export interface RewardsResponse {
  sectionedContentModel: SectionedContentScreenModel;
}

interface RewardsScreenState {
  loading: boolean;
  response?: Maybe<RewardsResponse>;
  errors?: string[];
  loadMore: () => void;
  reload: () => void;
  refreshAfterActivation: () => void;
  fetchRewardsData: (coordinates: google.maps.LatLngLiteral) => void;
}

const useRewardsHomeDataV2 = (shouldPause: boolean): RewardsScreenState => {
  const [coordinates, setCoordinates] = useState<
    google.maps.LatLngLiteral | undefined
  >(undefined);
  const [after, setAfter] = useState<string>('');

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'listItem',
  });
  const activateButtonLabel = translatedText('activateButton');

  const { recordImpressions, formatRewardsDataForKardImpressions } =
    useKardAttribution();
  const { selfFundedOffersSection } = useSelfFundedOffersV2();

  const { data, status, refetch } = useRewardsQueries({
    shouldPause,
    coordinates,
    after,
  });

  const {
    sections,
    fetchingMoreRef,
    isRefreshingRef,
    loadMore,
    startRefresh,
    clearSections,
  } = useRewardsPagination({
    sectionsData: data.sectionsData,
    after,
    setAfter,
  });

  const isLoading =
    status.isFetching && !fetchingMoreRef.current && !isRefreshingRef.current;
  const errors = status.hasErrors ? ['error'] : undefined;
  const promoSectionData =
    data.sectionsData?.myPartnerOffers?.perkSections?.promoSection;

  const isAllDataEmpty =
    !data?.topCategoriesData?.myPartnerOffers?.topCategories?.length &&
    !data?.cashbackEarnedData?.myPartnerOffers?.cashback &&
    !data?.activatedOffersData?.myPartnerOffers?.offers &&
    (!sections || sections.length === 0) &&
    !promoSectionData;

  const { nearbyRenderer } = useNearbySectionRenderer({
    coordinates,
    isLoading,
  });

  const fetchRewardsData = (newCoordinates: google.maps.LatLngLiteral) => {
    setCoordinates(newCoordinates);
  };

  const reload = (): void => {
    clearSections();
    refetch.refetchTopCategories({ requestPolicy: 'network-only' });
    refetch.refetchCashbackEarned({ requestPolicy: 'network-only' });
    refetch.refetchActivatedOffers({ requestPolicy: 'network-only' });
    refetch.refetchRewardsSections({ requestPolicy: 'network-only' });
  };

  const refreshAfterActivation = (): void => {
    startRefresh();
    refetch.refetchCashbackEarned({ requestPolicy: 'network-only' });
    refetch.refetchActivatedOffers({ requestPolicy: 'network-only' });
    if (after === '') {
      refetch.refetchRewardsSections({ requestPolicy: 'network-only' });
    }
  };

  useEffect(() => {
    if (data.sectionsData) {
      const kardAttributionData = formatRewardsDataForKardImpressions(
        data.sectionsData,
        'BROWSE',
      );
      void recordImpressions(EventType.Impression, kardAttributionData);
    }
  }, [
    formatRewardsDataForKardImpressions,
    recordImpressions,
    data.sectionsData,
  ]);

  const response = useMemo(
    () =>
      formatRewardsData(
        selfFundedOffersSection,
        sections,
        data.topCategoriesData,
        data.cashbackEarnedData,
        data.activatedOffersData,
        nearbyRenderer,
        coordinates,
        promoSectionData,
        activateButtonLabel,
        translatedText,
      ),
    [
      selfFundedOffersSection,
      sections,
      data.topCategoriesData,
      data.cashbackEarnedData,
      data.activatedOffersData,
      nearbyRenderer,
      coordinates,
      promoSectionData,
      activateButtonLabel,
      translatedText,
    ],
  );

  return {
    loading: isLoading,
    response,
    errors:
      !shouldPause && isAllDataEmpty && !status.isFetching ? ['error'] : errors,
    loadMore,
    reload,
    refreshAfterActivation,
    fetchRewardsData,
  };
};

export default useRewardsHomeDataV2;
